import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, KpiCard, Modal, Spinner, useToast,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertCircle, Calendar, IndianRupee, Plus, RefreshCw, Save, Search,
  Sparkles, Target, TrendingDown, TrendingUp, User, X,
} from 'lucide-react';
import { EntityPicker } from '../sales/EntityPicker';

/**
 * CRM sales pipeline — kanban board.
 *
 * Loads `OpportunityStage` for the column titles and `Opportunity` for
 * the cards, then lets a sales rep drag opportunities across stages.
 * Each card shows customer, amount, probability and close date.
 *
 * Drag implementation uses native HTML5 DnD — zero dependencies — and
 * the stage update is optimistic: card moves instantly, server PUT is
 * fire-and-forget with rollback on failure.
 */

interface Stage extends Record<string, unknown> {
  opportunityStageId?: string;
  id?: number | string;
  code?: string;
  name?: string;
  sortOrder?: number;
  probability?: number;
  category?: 'Open' | 'Won' | 'Lost' | string;
}

interface Opportunity extends Record<string, unknown> {
  opportunityId?: string;
  id?: number | string;
  code?: string;
  title?: string;
  name?: string;
  customerId?: string;
  customerName?: string;
  amount?: number;
  expectedAmount?: number;
  probability?: number;
  closeDate?: string;
  expectedCloseDate?: string;
  stageId?: string;
  opportunityStageId?: string;
  status?: string;
  ownerName?: string;
}

interface Customer extends Record<string, unknown> {
  customerId?: string;
  id?: number;
  name?: string;
  phone?: string;
}

const FALLBACK_STAGES: Stage[] = [
  { id: 'new',         code: 'NEW',  name: 'New',         sortOrder: 1, probability: 10, category: 'Open' },
  { id: 'qualified',   code: 'QLF',  name: 'Qualified',   sortOrder: 2, probability: 30, category: 'Open' },
  { id: 'proposal',    code: 'PROP', name: 'Proposal',    sortOrder: 3, probability: 50, category: 'Open' },
  { id: 'negotiation', code: 'NEG',  name: 'Negotiation', sortOrder: 4, probability: 75, category: 'Open' },
  { id: 'won',         code: 'WON',  name: 'Won',         sortOrder: 5, probability: 100, category: 'Won' },
  { id: 'lost',        code: 'LOST', name: 'Lost',        sortOrder: 6, probability: 0,  category: 'Lost' },
];

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function CrmPipeline() {
  const toast = useToast();
  const navigate = useNavigate();

  const [stages, setStages] = useState<Stage[]>([]);
  const [opps, setOpps] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [editingOpp, setEditingOpp] = useState<Opportunity | null | undefined>(undefined);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoverStageId, setHoverStageId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const [s, o] = await Promise.all([
        apiClient.get('/crm/OpportunityStage').then(pickList<Stage>).catch(() => []),
        apiClient.get('/crm/Opportunity').then(pickList<Opportunity>).catch(() => []),
      ]);
      setStages(s.length ? s.sort(byOrder) : FALLBACK_STAGES);
      setOpps(o);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load pipeline');
      setStages(FALLBACK_STAGES);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  // Group opportunities by stage. Apply text filter.
  const oppsByStage = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? opps.filter((o) =>
          [o.title, o.name, o.customerName, o.code, o.ownerName]
            .filter(Boolean)
            .map((v) => String(v).toLowerCase())
            .some((v) => v.includes(q)),
        )
      : opps;
    const m = new Map<string, Opportunity[]>();
    for (const s of stages) {
      m.set(stageKey(s), []);
    }
    for (const o of filtered) {
      const key = String(o.opportunityStageId ?? o.stageId ?? '');
      const found = m.get(key) ?? m.get(unmapStage(stages, o)) ?? [];
      const targetKey = m.has(key) ? key : unmapStage(stages, o);
      const arr = m.get(targetKey);
      if (arr) {
        arr.push(o);
      } else {
        // Park orphans under the first open stage (or first stage).
        const fallback = stages.find((s) => (s.category ?? 'Open') === 'Open') ?? stages[0];
        if (fallback) m.get(stageKey(fallback))?.push(o);
      }
    }
    return m;
  }, [stages, opps, search]);

  // KPIs
  const kpis = useMemo(() => {
    const open = opps.filter((o) => isOpen(o, stages));
    const wonThisMonth = opps.filter((o) => isWon(o, stages) && isThisMonth(o.closeDate ?? o.expectedCloseDate));
    const lost = opps.filter((o) => isLost(o, stages));
    const pipelineValue = open.reduce(
      (s, o) => s + (Number(o.amount ?? o.expectedAmount ?? 0) * (Number(o.probability ?? 50) / 100)),
      0,
    );
    const wonValue = wonThisMonth.reduce((s, o) => s + Number(o.amount ?? 0), 0);
    const totalDecisions = opps.filter((o) => isWon(o, stages) || isLost(o, stages)).length;
    const winRate =
      totalDecisions > 0 ? Math.round((opps.filter((o) => isWon(o, stages)).length / totalDecisions) * 100) : 0;
    return {
      pipelineValue,
      openCount: open.length,
      wonValue,
      wonCount: wonThisMonth.length,
      lostCount: lost.length,
      winRate,
    };
  }, [opps, stages]);

  // Drag and drop
  function onDragStart(id: string, e: React.DragEvent) {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
  }
  function onDragEnd() {
    setDraggingId(null);
    setHoverStageId(null);
  }
  function onColumnDragOver(stageId: string, e: React.DragEvent) {
    e.preventDefault();
    setHoverStageId(stageId);
  }
  async function onColumnDrop(stage: Stage, e: React.DragEvent) {
    e.preventDefault();
    setHoverStageId(null);
    const id = e.dataTransfer.getData('text/plain');
    if (!id) return;
    const opp = opps.find((o) => String(o.opportunityId ?? o.id) === id);
    if (!opp) return;
    const targetStageId = String(stage.opportunityStageId ?? stage.id ?? '');
    const currentStageId = String(opp.opportunityStageId ?? opp.stageId ?? '');
    if (currentStageId === targetStageId) return;

    // Optimistic update.
    setOpps((cur) =>
      cur.map((o) =>
        String(o.opportunityId ?? o.id) === id
          ? {
              ...o,
              opportunityStageId: targetStageId,
              stageId: targetStageId,
              status:
                stage.category === 'Won' ? 'Won' : stage.category === 'Lost' ? 'Lost' : (o.status ?? 'Open'),
              probability: typeof stage.probability === 'number' ? stage.probability : o.probability,
            }
          : o,
      ),
    );

    try {
      const numericTarget = /^\d+$/.test(targetStageId) ? Number(targetStageId) : null;
      const workflow =
        stage.category === 'Won' ? 'Won' : stage.category === 'Lost' ? 'Lost'
        : (typeof opp.opportunityStatus === 'string' ? opp.opportunityStatus : 'Open');
      const movePayload: Record<string, unknown> = {
        ...opp,
        ...(numericTarget != null ? { stageId: numericTarget } : {}),
        // numeric row status + string workflow stage (see save()).
        status: 0,
        opportunityStatus: workflow,
        probability: typeof stage.probability === 'number' ? stage.probability : opp.probability,
      };
      delete movePayload.isActive; // legacy service-managed; not a POST/PUT column
      await apiClient.put('/crm/Opportunity', movePayload);
      toast.success('Stage updated', `${title(opp)} → ${stage.name ?? '—'}`);
    } catch (e) {
      toast.error('Move failed', e instanceof Error ? e.message : 'Network error');
      // Rollback by reloading the source of truth.
      void load();
    }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Sales pipeline</h1>
          <p className="text-xs text-content-muted">
            {opps.length} opportunit{opps.length === 1 ? 'y' : 'ies'} across {stages.length} stage(s)
          </p>
        </div>
        <div className="relative">
          <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-content-muted" />
          <input
            className={`${inputCls} pl-8`}
            placeholder="Search title / customer / owner"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 260 }}
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        <Button size="sm" onClick={() => setEditingOpp(null)}>
          <Plus className="h-3.5 w-3.5" /> New opportunity
        </Button>
      </div>

      {err && <Alert variant="warning">{err} — falling back to default stages.</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Pipeline value"
          value={`₹ ${inr(kpis.pipelineValue)}`}
          subtitle="Probability-weighted, open only"
          icon={<Target className="h-5 w-5 text-primary" />}
        />
        <KpiCard
          title="Open opportunities"
          value={kpis.openCount.toLocaleString()}
          subtitle="All non-final stages"
          icon={<Sparkles className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Won this month"
          value={`₹ ${inr(kpis.wonValue)}`}
          subtitle={`${kpis.wonCount} deal(s)`}
          icon={<TrendingUp className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="Win rate"
          value={`${kpis.winRate}%`}
          subtitle={`${kpis.lostCount} lost · ${kpis.openCount + kpis.wonCount + kpis.lostCount} total`}
          icon={<IndianRupee className={`h-5 w-5 ${kpis.winRate >= 50 ? 'text-success' : 'text-warning'}`} />}
        />
      </div>

      {loading && opps.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading pipeline…
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto pb-3">
          <div className="flex min-w-fit gap-3">
            {stages.map((s) => {
              const stageId = stageKey(s);
              const stageOpps = oppsByStage.get(stageId) ?? [];
              const stageValue = stageOpps.reduce(
                (sum, o) => sum + Number(o.amount ?? o.expectedAmount ?? 0),
                0,
              );
              const isHover = hoverStageId === stageId;
              return (
                <div
                  key={stageId}
                  className={`flex min-h-[440px] w-72 flex-shrink-0 flex-col rounded-lg border-2 ${
                    isHover ? 'border-primary' : 'border-border'
                  } bg-surface-card transition-colors`}
                  onDragOver={(e) => onColumnDragOver(stageId, e)}
                  onDragLeave={() => setHoverStageId(null)}
                  onDrop={(e) => onColumnDrop(s, e)}
                >
                  <ColumnHeader stage={s} count={stageOpps.length} value={stageValue} />
                  <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-2">
                    {stageOpps.length === 0 ? (
                      <div className="flex h-full min-h-[100px] items-center justify-center rounded-md border border-dashed border-border/60 text-[11px] text-content-muted">
                        Drop here
                      </div>
                    ) : (
                      stageOpps.map((o) => (
                        <OppCard
                          key={String(o.opportunityId ?? o.id)}
                          opp={o}
                          dragging={draggingId === String(o.opportunityId ?? o.id)}
                          onDragStart={(e) => onDragStart(String(o.opportunityId ?? o.id ?? ''), e)}
                          onDragEnd={onDragEnd}
                          onClick={() => setEditingOpp(o)}
                        />
                      ))
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingOpp({
                        opportunityStageId: stageId,
                        stageId,
                        probability: typeof s.probability === 'number' ? s.probability : 50,
                      });
                    }}
                    className="m-2 flex h-8 items-center justify-center gap-1 rounded-md border border-dashed border-border text-xs text-content-secondary hover:border-primary hover:text-primary"
                  >
                    <Plus size={12} /> Add to {s.name ?? 'stage'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <OpportunityForm
        open={editingOpp !== undefined}
        opp={editingOpp ?? null}
        stages={stages}
        onClose={() => setEditingOpp(undefined)}
        onSaved={() => {
          setEditingOpp(undefined);
          void load();
        }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Column + card                                                       */
/* -------------------------------------------------------------------- */

function ColumnHeader({ stage, count, value }: { stage: Stage; count: number; value: number }) {
  const cat = stage.category ?? 'Open';
  const accent =
    cat === 'Won' ? 'border-success/40 bg-success/10 text-success'
      : cat === 'Lost' ? 'border-danger/40 bg-danger/10 text-danger'
      : 'border-info/40 bg-info/10 text-info';
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
      <div className="flex items-center gap-2 truncate">
        <span className={`inline-flex h-5 items-center rounded-full border px-2 text-[10px] uppercase tracking-wider ${accent}`}>
          {stage.name ?? '—'}
        </span>
        {typeof stage.probability === 'number' && (
          <span className="text-[10px] text-content-muted">{stage.probability}%</span>
        )}
      </div>
      <div className="text-right text-[11px]">
        <div className="font-semibold tabular-nums text-content-primary">{count}</div>
        <div className="text-content-muted tabular-nums">₹ {inr(value)}</div>
      </div>
    </div>
  );
}

function OppCard({
  opp, dragging, onDragStart, onDragEnd, onClick,
}: {
  opp: Opportunity;
  dragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onClick: () => void;
}) {
  const amount = Number(opp.amount ?? opp.expectedAmount ?? 0);
  const closeAt = opp.closeDate ?? opp.expectedCloseDate;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`cursor-grab rounded-md border border-border bg-surface-card p-2.5 transition active:cursor-grabbing ${
        dragging ? 'opacity-50' : 'hover:border-primary'
      }`}
    >
      <div className="line-clamp-2 text-sm font-medium text-content-primary">{title(opp)}</div>
      {opp.customerName && (
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-content-secondary">
          <User size={10} />
          <span className="truncate">{opp.customerName}</span>
        </div>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <span className="font-semibold tabular-nums text-primary">₹ {inr(amount)}</span>
        {typeof opp.probability === 'number' && (
          <span className="rounded-full bg-content-muted/10 px-1.5 py-0.5 text-content-muted">
            {opp.probability}%
          </span>
        )}
      </div>
      {closeAt && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-content-muted">
          <Calendar size={10} />
          {fmtDate(closeAt)}
          {isOverdue(closeAt) && <AlertCircle size={10} className="text-danger" />}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Quick-edit drawer                                                   */
/* -------------------------------------------------------------------- */

interface OppFormProps {
  open: boolean;
  opp: Opportunity | null;
  stages: Stage[];
  onClose: () => void;
  onSaved: () => void;
}

function OpportunityForm({ open, opp, stages, onClose, onSaved }: OppFormProps) {
  const isEdit = !!opp?.opportunityId || !!opp?.id;
  const toast = useToast();

  const [titleField, setTitleField] = useState('');
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [amount, setAmount] = useState<number | ''>('');
  const [probability, setProbability] = useState<number>(50);
  const [closeDate, setCloseDate] = useState('');
  const [stageId, setStageId] = useState<string>('');
  const [ownerName, setOwnerName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Reset on open
  const lastKey = useRef<string>('');
  useEffect(() => {
    if (!open) return;
    const key = JSON.stringify(opp ?? {});
    if (key === lastKey.current) return;
    lastKey.current = key;
    setTitleField(String(opp?.title ?? opp?.name ?? ''));
    setCustomer(
      opp?.customerId
        ? { customerId: String(opp.customerId), name: String(opp.customerName ?? '') }
        : null,
    );
    setAmount(typeof opp?.amount === 'number' ? opp.amount : '');
    setProbability(Number(opp?.probability ?? 50));
    setCloseDate(String(opp?.closeDate ?? opp?.expectedCloseDate ?? '').slice(0, 10));
    setStageId(String(opp?.opportunityStageId ?? opp?.stageId ?? stages[0]?.id ?? ''));
    setOwnerName(String(opp?.ownerName ?? ''));
    setErr(null);
  }, [open, opp, stages]);

  async function save() {
    if (!titleField.trim()) {
      setErr('Title is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const stage = stages.find((s) => stageKey(s) === stageId);
      // §35: row-level `status` is the numeric enum (0=Active). The workflow
      // stage (Open/Won/Lost) belongs in the STRING `opportunityStatus`
      // column — sending a string to `status` 400s with invalid_body.
      const workflow = stage?.category === 'Won' ? 'Won' : stage?.category === 'Lost' ? 'Lost' : 'Open';
      // `stageId` is a nullable int FK → OpportunityStage. Only send it when
      // we actually have a numeric id (fallback stages use string keys).
      const numericStage = /^\d+$/.test(String(stageId)) ? Number(stageId) : null;
      const payload = {
        ...(opp ?? {}),
        // Schema column is `opportunityName` (+ generic `name`); there is no
        // `title` column, so the old payload dropped the title silently.
        opportunityName: titleField,
        name: titleField,
        customerId: customer?.customerId ?? customer?.id ?? null,
        customerName: customer?.name ?? null,
        amount: amount === '' ? 0 : Number(amount),
        probability: Number(probability) || 0,
        // Schema column is `expectedCloseDate` (date-time), not `closeDate`.
        expectedCloseDate: closeDate ? new Date(closeDate).toISOString() : null,
        ...(numericStage != null ? { stageId: numericStage } : {}),
        status: 0,
        opportunityStatus: workflow,
        notes: ownerName ? `Owner: ${ownerName}` : (opp?.notes ?? undefined),
      };
      // Strip legacy keys that aren't schema columns (would be dropped, and
      // `title`/`isActive`/`closeDate` could shadow the correct ones above).
      delete (payload as Record<string, unknown>).title;
      delete (payload as Record<string, unknown>).isActive;
      delete (payload as Record<string, unknown>).closeDate;
      delete (payload as Record<string, unknown>).ownerName;
      if (isEdit) {
        await apiClient.put('/crm/Opportunity', payload);
        toast.success('Saved', `${titleField} updated`);
      } else {
        await apiClient.post('/crm/Opportunity', payload);
        toast.success('Saved', `${titleField} created`);
      }
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Save failed';
      setErr(msg);
      toast.error('Save failed', msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit opportunity' : 'New opportunity'} width={560}>
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}

      <div className="grid gap-3 md:grid-cols-2">
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-xs font-medium text-content-secondary">Title <span className="text-danger">*</span></span>
          <input
            className={inputCls}
            value={titleField}
            onChange={(e) => setTitleField(e.target.value)}
            placeholder="Acme Retail · Q3 expansion"
          />
        </label>

        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-xs font-medium text-content-secondary">Customer</span>
          <EntityPicker<Customer>
            basePath="/crm/Customer"
            idKey="customerId"
            labelKey="name"
            secondaryKey="phone"
            value={(customer?.customerId ?? customer?.id) as string | number | null}
            onChange={setCustomer}
            placeholder="Search customer"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">Amount (₹)</span>
          <input
            type="number"
            className={inputCls}
            value={amount}
            onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
            min={0}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">Probability (%)</span>
          <input
            type="number"
            className={inputCls}
            value={probability}
            onChange={(e) => setProbability(Math.min(100, Math.max(0, Number(e.target.value))))}
            min={0}
            max={100}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">Expected close</span>
          <input
            type="date"
            className={inputCls}
            value={closeDate}
            onChange={(e) => setCloseDate(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-content-secondary">Stage</span>
          <select
            className={inputCls}
            value={stageId}
            onChange={(e) => {
              const next = e.target.value;
              setStageId(next);
              const s = stages.find((x) => stageKey(x) === next);
              if (s && typeof s.probability === 'number') setProbability(s.probability);
            }}
          >
            {stages.map((s) => (
              <option key={stageKey(s)} value={stageKey(s)}>
                {s.name ?? '—'}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 md:col-span-2">
          <span className="text-xs font-medium text-content-secondary">Owner</span>
          <input
            className={inputCls}
            value={ownerName}
            onChange={(e) => setOwnerName(e.target.value)}
            placeholder="Sales rep name"
          />
        </label>
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-3">
        <Button variant="outline" onClick={onClose} disabled={busy}>
          <X className="h-4 w-4" /> Cancel
        </Button>
        <Button onClick={save} disabled={busy}>
          <Save className="h-4 w-4" /> {busy ? 'Saving…' : isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

function stageKey(s: Stage): string {
  return String(s.opportunityStageId ?? s.id ?? s.code ?? s.name ?? '');
}

function byOrder(a: Stage, b: Stage): number {
  return Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0);
}

function unmapStage(stages: Stage[], o: Opportunity): string {
  // Map any "status" hint onto a stage when the stage id is missing.
  const status = String(o.status ?? '').toLowerCase();
  if (status === 'won') {
    const won = stages.find((s) => (s.category ?? '').toLowerCase() === 'won');
    if (won) return stageKey(won);
  }
  if (status === 'lost') {
    const lost = stages.find((s) => (s.category ?? '').toLowerCase() === 'lost');
    if (lost) return stageKey(lost);
  }
  return stages[0] ? stageKey(stages[0]) : '';
}

function isOpen(o: Opportunity, stages: Stage[]): boolean {
  const s = stages.find((x) => stageKey(x) === String(o.opportunityStageId ?? o.stageId ?? ''));
  const cat = (s?.category ?? '').toLowerCase();
  if (cat) return cat === 'open';
  const st = String(o.status ?? '').toLowerCase();
  return st !== 'won' && st !== 'lost' && st !== 'closed' && st !== 'cancelled';
}

function isWon(o: Opportunity, stages: Stage[]): boolean {
  const s = stages.find((x) => stageKey(x) === String(o.opportunityStageId ?? o.stageId ?? ''));
  if ((s?.category ?? '').toLowerCase() === 'won') return true;
  return String(o.status ?? '').toLowerCase() === 'won';
}

function isLost(o: Opportunity, stages: Stage[]): boolean {
  const s = stages.find((x) => stageKey(x) === String(o.opportunityStageId ?? o.stageId ?? ''));
  if ((s?.category ?? '').toLowerCase() === 'lost') return true;
  return String(o.status ?? '').toLowerCase() === 'lost';
}

function isThisMonth(d: unknown): boolean {
  if (typeof d !== 'string') return false;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return false;
  const now = new Date();
  return dt.getFullYear() === now.getFullYear() && dt.getMonth() === now.getMonth();
}

function isOverdue(d: unknown): boolean {
  if (typeof d !== 'string') return false;
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return false;
  return dt.getTime() < Date.now();
}

function title(o: Opportunity): string {
  return String(o.title ?? o.name ?? o.code ?? '—');
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}
// Suppress unused-import: TrendingDown reserved for a future "drop" pill.
const _ = TrendingDown;
